import { FC, useEffect } from 'react';

import { MaxWidth } from '@eneco/components/MaxWidth';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { TrackedPopover } from '@eneco/components/TrackedPopover/TrackedPopover';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Checkbox, CheckboxGroup, Heading, Stack, Divider, TextArea, TextLink, Text } from '@eneco/sparky';
import { InfoIcon } from '@eneco/sparky/icons';
import { usePlaceholderContent } from '@sitecore/common';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
  createSelectedProductsMap,
  getEnergyManagementProducts,
  getEnergyTypeProducts,
  getProductFromParam,
  getSustainabilityProducts,
  isProductSelected,
} from '../../helpers';
import { useFlowTracking } from '../../hooks/useFlowTracking';
import {
  AvailableProducts,
  B2BLeadFlowContext,
  ChoiceOption,
  EnergyManagementProducts,
  SustainabilityProducts,
  EnergyTypeProducts,
  FixedB2BLeadFlowProductSelectionStepRendering,
} from '../../types';

interface FormFields {
  energyManagementProducts: EnergyManagementProducts[];
  energyTypeProducts: EnergyTypeProducts[];
  sustainabilityProducts: SustainabilityProducts[];
  description: string;
  otherOptions: boolean;
}
const energyManagementProductsField = 'energyManagementProducts';
const energyTypeProductsField = 'energyTypeProducts';
const sustainabilityProductsField = 'sustainabilityProducts';
const descriptionField = 'description';
const otherOptionsField = 'otherOptions';
const selectOptionErrorMessage = 'Maak een keuze voor uw offerte of advies';

export const ProductSelectionStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<B2BLeadFlowContext>();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const { trackAddToCart } = useFlowTracking();
  const data = usePlaceholderContent<{
    B2BLeadFlowProductSelectionStep: FixedB2BLeadFlowProductSelectionStepRendering;
  }>();
  const { B2BLeadFlowProductSelectionStep: { fields: textualData } = {} } = data;
  const descriptionRequiredMessage = textualData?.otherChoice?.messageInputField?.value?.requiredMessage ?? '';
  const businessTypeStepSchema = yup
    .object()
    .shape({
      [energyManagementProductsField]: yup.array().of(yup.string()),
      [energyTypeProductsField]: yup.array().of(yup.string()),
      [sustainabilityProductsField]: yup.array().of(yup.string()),
      [descriptionField]: yup.string(),
      [otherOptionsField]: yup.boolean(),
    })
    .test('at-least-one-option', selectOptionErrorMessage, function (values) {
      const hasEnergyManagement = (values?.energyManagementProducts?.length ?? 0) > 0;
      const hasEnergyType = (values?.energyTypeProducts?.length ?? 0) > 0;
      const hasSustainability = (values?.sustainabilityProducts?.length ?? 0) > 0;
      const hasOther = values?.otherOptions === true;

      if (hasEnergyManagement || hasEnergyType || hasSustainability || hasOther) {
        return true;
      }

      return this.createError({
        path: 'productsNotSelected',
        message: selectOptionErrorMessage,
      });
    })
    .test('description-required', descriptionRequiredMessage, function (values) {
      const hasOther = values?.otherOptions === true;
      const hasDescription = (values?.description?.trim().length ?? 0) > 0;

      if (!hasOther || hasDescription) {
        return true;
      }

      return this.createError({
        path: descriptionField,
        message: descriptionRequiredMessage,
      });
    });
  const preselectedProduct = getProductFromParam(flowContext.product || '');
  const effectiveSelectedProducts = preselectedProduct
    ? { ...flowContext.selectedProducts, [preselectedProduct]: '1' as const }
    : flowContext.selectedProducts;

  const {
    handleSubmit,
    control,
    watch,
    register,
    getValues,
    clearErrors,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(businessTypeStepSchema),
    defaultValues: {
      [energyTypeProductsField]: getEnergyTypeProducts(effectiveSelectedProducts),
      [sustainabilityProductsField]: getSustainabilityProducts(effectiveSelectedProducts),
      [energyManagementProductsField]: getEnergyManagementProducts(effectiveSelectedProducts),
      [otherOptionsField]: isProductSelected(effectiveSelectedProducts, 'enc_ietsanders'),
      [descriptionField]: flowContext.otherRequestDescription,
    },
  });

  const isOtherOptions = watch(otherOptionsField);
  const watchedEnergyManagement = watch(energyManagementProductsField);
  const watchedEnergyType = watch(energyTypeProductsField);
  const watchedSustainability = watch(sustainabilityProductsField);

  useEffect(() => {
    if (flowContext.product) {
      sendToFlowMachine({ type: 'UPDATE_VALUES', values: { ...flowContext, product: undefined } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasAnySelection =
      watchedEnergyManagement.length > 0 ||
      watchedEnergyType.length > 0 ||
      watchedSustainability.length > 0 ||
      isOtherOptions;

    if (hasAnySelection) {
      clearErrors('productsNotSelected' as keyof FormFields);
    }
  }, [watchedEnergyManagement, watchedEnergyType, watchedSustainability, isOtherOptions, clearErrors]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const { energyManagementProducts, energyTypeProducts, sustainabilityProducts, description, otherOptions } =
        getValues();
      const selectedProducts: AvailableProducts[] = [
        ...energyManagementProducts,
        ...energyTypeProducts,
        ...sustainabilityProducts,
      ];

      if (otherOptions) {
        selectedProducts.push('enc_ietsanders');
      }

      trackAddToCart(selectedProducts);

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...flowContext,
          selectedProducts: createSelectedProductsMap(selectedProducts),
          otherOptions,
          otherRequestDescription: otherOptions ? description : '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const handleChange = (name: string, value: string[], onChange: (...event: unknown[]) => void) =>
    value.includes(name) ? onChange(value.filter(val => val !== name)) : onChange([...value, name]);
  const getFormField = (
    value: string[],
    onChange: (...event: unknown[]) => void,
    formField: ChoiceOption['fields']['formField'] | undefined,
  ) => {
    if (!formField) return null;
    const nameValue = formField.name?.value as unknown as string | undefined;
    const labelValue = formField.label?.value as unknown as string | undefined;
    const hintValue = formField.hint?.value as unknown as string | undefined;
    return (
      <div key={nameValue}>
        <Stack direction="row" gap={2}>
          <Stack.Item grow>
            <Checkbox
              label={labelValue ?? ''}
              name={nameValue ?? ''}
              isChecked={value?.includes(nameValue ?? '')}
              onChange={() => handleChange(nameValue ?? '', value, onChange)}
            />
          </Stack.Item>
          {hintValue && (
            <TrackedPopover
              side="right"
              size={'small'}
              title={hintValue ?? ''}
              trigger={<TextLink leftIcon={<InfoIcon color="iconSecondary" />}>{''}</TextLink>}>
              <Text weight="regular">
                <RichText html={hintValue} />
              </Text>
            </TrackedPopover>
          )}
        </Stack>
      </div>
    );
  };

  const productsNotSelected = (errors as Record<string, { message?: string }>)?.productsNotSelected;
  const notSelectedErrorMessage = productsNotSelected?.message ?? '';
  const descriptionErrorMessage = errors.description?.message ?? '';
  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Stack gap={10}>
        <Header>
          <Heading as={'h2'} size={'M'}>
            {textualData?.content?.title?.value}
          </Heading>
          {textualData?.content?.text?.value && <RichText html={textualData?.content?.text?.value} />}
        </Header>
        {notSelectedErrorMessage && (
          <TrackedNotificationBox
            label={notSelectedErrorMessage}
            isAlert
            text={notSelectedErrorMessage}
            variant="error"
          />
        )}
        <Stack.Item>
          <Stack gap={4}>
            <Heading as={'h2'} size={'S'}>
              Energie
            </Heading>
            <MaxWidth maxWidth="320">
              <Controller
                control={control}
                name={energyTypeProductsField}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup label="">
                    {null}
                    {textualData?.choice.energyType?.map(item => getFormField(value, onChange, item.fields?.formField))}
                  </CheckboxGroup>
                )}
              />
            </MaxWidth>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack gap={4}>
            <Heading as={'h2'} size={'S'}>
              Verduurzamen
            </Heading>
            <MaxWidth maxWidth="320">
              <Controller
                control={control}
                name={sustainabilityProductsField}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup label="">
                    {null}
                    {textualData?.choice.sustainabilityOptions?.map(item =>
                      getFormField(value, onChange, item.fields?.formField),
                    )}
                  </CheckboxGroup>
                )}
              />
            </MaxWidth>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack gap={4}>
            <Heading as={'h2'} size={'S'}>
              Energiesturing
            </Heading>
            <MaxWidth maxWidth="320">
              <Controller
                control={control}
                name={energyManagementProductsField}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup label="">
                    {null}
                    {textualData?.choice.energyManagementOptions?.map(item =>
                      getFormField(value, onChange, item.fields?.formField),
                    )}
                  </CheckboxGroup>
                )}
              />
            </MaxWidth>
          </Stack>
        </Stack.Item>
        <Divider />
        <Stack.Item>
          <Stack gap={4}>
            <Checkbox
              {...register(otherOptionsField)}
              label={textualData?.otherChoice?.checkboxInputField?.value?.label ?? ''}
              hint={textualData?.otherChoice?.checkboxInputField?.value?.hint}
            />
            {isOtherOptions && (
              <TextArea
                {...register('description')}
                label={''}
                error={descriptionErrorMessage}
                placeholder={textualData?.otherChoice.messageInputField.value.placeholder}
                rows={5}
              />
            )}
          </Stack>
        </Stack.Item>
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
      </Stack>
    </Layout.Content>
  );
};
